import type { ReadingRiskResult } from "./ReadingRiskUtils";

export type ReadingInsightSuggestion = {
  title: string;
  description: string;
};

export function getReadingInsightSuggestions(risk: ReadingRiskResult): ReadingInsightSuggestion[] {
  if (risk.isInconclusive) {
    return [
      {
        title: "Repeat the assessment",
        description:
          "Comprehension validity was low, so repeat the reading task before drawing conclusions from the eye-movement pattern.",
      },
      {
        title: "Confirm task setup",
        description:
          "Check that the patient understood the instructions, could see the text clearly, and completed the full passage attentively.",
      },
    ];
  }

  const suggestions: ReadingInsightSuggestion[] = [
    {
      title: "Review the metric profile",
      description:
        "Use the risk-factor cards to identify whether speed, fixation frequency, regressions, or fixation duration is driving the pattern.",
    },
  ];

  if (risk.metrics.speed.points > 0) {
    suggestions.push({
      title: "Support reading fluency",
      description:
        "Consider timed repeated reading, guided oral reading, or short fluency drills matched to the patient's age and language level.",
    });
  }

  if (risk.metrics.fixations.points > 0 || risk.metrics.duration.points > 0) {
    suggestions.push({
      title: "Reduce word-by-word processing load",
      description:
        "Use larger text spacing, shorter reading blocks, and structured breaks while monitoring whether fixation effort decreases.",
    });
  }

  if (risk.metrics.regressions.points > 0) {
    suggestions.push({
      title: "Track re-reading behavior",
      description:
        "Frequent backward movements can reflect decoding or comprehension load; consider follow-up with reading comprehension and decoding checks.",
    });
  }

  if (risk.totalScore <= 4) {
    suggestions.push({
      title: "Maintain routine monitoring",
      description:
        "Current eye-movement metrics are within the expected range. Reassess if reading fatigue, avoidance, headaches, or school performance concerns emerge.",
    });
  } else if (risk.totalScore <= 6) {
    suggestions.push({
      title: "Monitor and recheck",
      description:
        "Monitor reading ability alongside history and symptoms; consider repeating the assessment after targeted reading support.",
    });
  } else {
    suggestions.push({
      title: "Consider specialist review",
      description:
        "High-risk reading patterns should be reviewed alongside clinical history, educational concerns, and complementary reading evaluations.",
    });
  }

  return suggestions.slice(0, 4);
}
